import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { OrderCard } from "@/features/orders/OrderCard";
import { getOrder, ordersKeys } from "@/features/orders/orders.api";
import { Skeleton } from "@/components/feedback/Skeleton";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/orders/$id")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_orders") && !hasModule(context.session, "tms_orders_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  component: OrderCardScreen,
});

function OrderCardScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { session } = Route.useRouteContext();
  const isAdmin = session.roles.includes("admin");
  const canEdit = isAdmin || hasModule(session, "tms_orders_write");
  // The trip card and the planning board ask for the planning right; without it a trip
  // number on the card is text, not a link to a page that sends them away.
  const canOpenTrips = hasModule(session, "tms_planning") || hasModule(session, "tms_planning_write");
  // What comes off a trip is the planner's to decide (04-10-2026): carrying out a cancellation or change that waits.
  const canPlan = isAdmin || hasModule(session, "tms_planning_write");
  const canOpenDossier = hasModule(session, "dossiers");
  // A price by hand (docs/tms-facturatie.md §24.8): orderentry or the tarifeerder sets it, planning approves it.
  const priceAccess = {
    canSet: isAdmin || hasModule(session, "tms_orders_write") || hasModule(session, "billing_write"),
    canApprove: isAdmin || hasModule(session, "tms_planning_write"),
    userId: session.userId,
  };

  const query = useQuery({
    queryKey: ordersKeys.detail(id),
    queryFn: () => getOrder(id),
    retry: false,
  });

  // Head, band, first stop — the shape the card itself has (handoff §7.3).
  if (query.isPending) {
    return (
      <div className="wl-page wl-page--fill" data-screen-label="Back-office Order" aria-busy="true">
        <Skeleton width="80px" height="var(--space-3)" />
        <div style={{ marginTop: "var(--space-2_5)" }}>
          <Skeleton width="280px" height="var(--space-7)" />
        </div>
        <div style={{ marginTop: "var(--space-4)" }}>
          <Skeleton height="var(--space-16)" />
        </div>
        <div style={{ marginTop: "var(--space-4)" }}>
          <Skeleton height="320px" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Order">
        <h1 className="t-h2">{t("orders.notFound")}</h1>
        <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
          {t("orders.notFoundDesc")}
        </p>
      </div>
    );
  }

  // The card draws its own head, band and columns edge to edge, so it gets no page
  // padding of its own; only the columns under the tabs breathe.
  return (
    <div data-screen-label="Back-office Order">
      <OrderCard
        order={query.data}
        canEdit={canEdit}
        isAdmin={isAdmin}
        canOpenTrips={canOpenTrips}
        canPlan={canPlan}
        canOpenDossier={canOpenDossier}
        priceAccess={priceAccess}
      />
    </div>
  );
}
